import axios from 'axios';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { Motion } from '@capacitor/motion';
import { useThrottle } from "@uidotdev/usehooks";

const UIContext = React.createContext(null);

const baseApiUrl = import.meta.env.VITE_ENC_API_URL_PREFIX;
const apiToken = import.meta.env.VITE_VECTOR_CHARTS_API_TOKEN;

export default UIContext;

export function UIContextProvider({ children }) {
    const [searchQuery, setSearchQuery] = useState(null);
    const [searchResults, setSearchResults] = useState(null);
    const [forceReload, setForceReload] = useState(null);
    const [attributionsOpen, setAttributionsOpen] = useState(false);
    const [mobileToolbarOpen, setMobileToolbarOpen] = useState(false);
    const [locationTracking, _setLocationTracking] = useState(false);
    const [trackInternal, setTrack] = useState(null);
    const watchRef = useRef(null);
    const orientationWatchRef = useRef(null);

    const setLocationTracking = (tracking) => {
        setTrack(null);
        _setLocationTracking(tracking);
    };

    const track = locationTracking ? trackInternal : null;

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        const setupLocationTracking = async () => {
            if (watchRef.current) {
                watchRef.current = null;
                await Geolocation.clearWatch(watchRef.current);
            }
            // if (orientationWatchRef.current) {
            //     orientationWatchRef.current = null;
            //     await Motion.removeAllListeners();
            // }
            await Motion.removeAllListeners();
            
            if (!locationTracking) {
                console.log("Stopped location tracking.");
                setTrack(null);
                return;
            }

            try {
                let permissions = await Geolocation.checkPermissions();
                // let motionPermission = await Motion.requestPermission();
                // console.lo
                // orientationWatchRef.current = await Motion.addListener('orientation', event => {
                //     console.log('Device orientation event:', event);
                // });

                if (permissions.location != "granted") {
                    const result = await Geolocation.requestPermissions('location');
                    console.log(result);

                    if (result.location != "granted") {
                        throw Exception("Locations not granted.");
                    }
                }

                console.log("Started location tracking!");
                const startTime = new Date().valueOf();
                setTrack({
                    startTime: startTime,
                    history: [],
                    latest: null
                });
                
                await Motion.addListener('accel', event => {
                    console.log('Device motion event:', event);
                });
                await Motion.addListener('orientation', event => {
                    console.log('Device motion event:', event);
                });

                watchRef.current = await Geolocation.watchPosition({
                    enableHighAccuracy: true,
                    timeout: 100,
                    minimumUpdateInterval: 100
                }, (position, err) => {
                    setTrack((old) => {
                        const latestPoint = {
                            position: [position.coords.latitude, position.coords.longitude],
                            heading: position.coords.heading,
                            altitude: position.coords.altitude,
                            timestamp: new Date().valueOf()
                        };
                        const history = [];
                        if (old?.history) {
                            history.push(...old.history);
                        } 
                        history.push(latestPoint);
                        
                        return ({
                            startTime: startTime,
                            latest: latestPoint,
                            history: history
                        });
                    });
                })
            } catch (e) {
                console.log("Failed to start location tracking :(");
                console.log(e);
            }
        };
        setupLocationTracking();
    }, [locationTracking]);

    const isIosApp = Capacitor.isNativePlatform();
    const insets = {
        bottom: 0,
        top: 0,
        left: 0,
        right: 0
    }
    if (isIosApp) {
        insets.top = getComputedStyle(document.documentElement).getPropertyValue("--sat");
        insets.bottom = getComputedStyle(document.documentElement).getPropertyValue("--sab");
        insets.right = getComputedStyle(document.documentElement).getPropertyValue("--sar");
        insets.left = getComputedStyle(document.documentElement).getPropertyValue("--sal");
    }

    const searchParams = new URLSearchParams(window.location.search);

    const history = useHistory();

    const queryElevation = async (data) => {
        const result = await axios.post(`${baseApiUrl}/api/v2/query/elevation?token=${apiToken}`, data);
        if (result.status == 200) {
            return result.data;
        }
    };

    const setBlob = async (data) => {
        const result = await axios.post(`${baseApiUrl}/api/v1/blobs?token=${apiToken}`, data);
        if (result.status == 200) {
            return result.data;
        }
    };
    const getBlob = async (id) => {
        const result = await axios.get(`${baseApiUrl}/api/v1/blobs/${id}?token=${apiToken}`);
        if (result.status == 200) {
            return result?.data?.value || null;
        } else {
            return null;
        }
    };
    const [selectedSearchResult, setSelectedSearchResult] = useState(null);

    const getAttributions = async () => {
        const result = await axios.get(`${baseApiUrl}/api/v1/datasets/attributions`);
        if (result.status == 200) {
            return result?.data || null;
        } else {
            return null;
        }
    };

    const makeGetRequest = async (url) => {
        const result = await axios.get(url);
        return result;
    };
    window.makeGetRequest = makeGetRequest;

    useEffect(() => {
        const updateBlobFromUrl = async () => {
            // Load blob initially.
            const params = new URLSearchParams(window.location.search);
            const sid = params.get('sid') || null;
            if (sid) {
                const value = await getBlob(sid);
                setSelectedSearchResult(value);
            }
        };
        updateBlobFromUrl();
    }, [forceReload]);

    const commitSelectedSearchResult = async (data) => {
        setSelectedSearchResult(data || null);
        const params = new URLSearchParams(window.location.search);
        if (data) {
            const { id } = await setBlob(data);
            params.set('sid', id);
        } else {
            params.delete('sid');
        }

        history.replace({ pathname: window.location.pathname, search: params.toString() });   
    };

    const [searchLoading, setSearchLoading] = useState(false);
    const searchInternalState = useRef({
        active: false,
    });
    const startSearch = async () => {
        setSearchLoading(true);
        setSearchResults(null);

        const searchResult = await axios.post(`${baseApiUrl}/api/v1/enc_query?token=${apiToken}`, searchQuery);
        console.log(searchResult);
        if (searchResult.status == 200) {
            setSearchResults(searchResult.data);
            setSearchLoading(false);
        }
    };
    useEffect(() => {
        if (searchQuery) {
            startSearch();
        } else {
            setSearchLoading(false);
        }
    }, [searchQuery]);


    const [routeInputWaypoints, setRouteInputWaypoints] = useState([]);
    const [routeResults, setRouteResults] = useState(null);
    const [routeSafetyMargin, setRouteSafetyMargin] = useState(0.5);
    const [routeSimplify, setRouteSimplify] = useState(true);
    const [routeSimplifyMaxSkipPoints, setRouteSimplifyMaxSkipPoints] = useState(5);
    const latestRouteRequest = useRef(new Date().valueOf());

    const throttledRouteInputWaypoints = useThrottle(routeInputWaypoints, 1000);

    const startRouting = async () => {
        // latestRouteRequest.current = new Date().valueOf();
        const thisRequest = new Date().valueOf();

        setRouteResults({
            loading: true
        });

        console.log(`Started routing (${throttledRouteInputWaypoints.length} waypoints)`);
        console.log(throttledRouteInputWaypoints);

        try {
            setRouteResults({
                loading: true
            });
            const routeResult = await axios.post(`${baseApiUrl}/api/v1/routing/route?token=${apiToken}`, {
                waypoints: throttledRouteInputWaypoints.map((pt) => ([pt.latitude, pt.longitude])),
                simplify: routeSimplify,
                simplifyMaxSkipPoints: routeSimplifyMaxSkipPoints,
                weights: {
                    depthWeight: routeSafetyMargin,
                    fairwayWeight: routeSafetyMargin,
                    obstructionWeight: routeSafetyMargin,
                    shoreWeight: routeSafetyMargin,
                    buoyWeight: routeSafetyMargin,
                    anchorageWeight: routeSafetyMargin,
                }
            });

            if (latestRouteRequest.current > thisRequest) {
                // Preempted by a newer request. Ignore!
                return;
            }
            // console.log(routeResult);
            if (routeResult.status == 200) {
                setRouteResults({
                    ...routeResult.data,
                    loading: false
                });
            } else {
                setRouteResults({
                    loading: false,
                    success: false,
                    messages: [{
                        message: "Failed to route."
                    }],
                    route: []
                });
            }
        } catch (e) {
            // console.log(e);
            setRouteResults({
                loading: false,
                success: false,
                messages: [{
                    message: "Failed to route."
                }],
                route: []
            });
        }
    };

    useEffect(() => {
        latestRouteRequest.current = new Date().valueOf();
        setRouteResults({
            loading: true
        });
    }, [routeInputWaypoints, routeSimplify, routeSafetyMargin]);

    useEffect(() => {
        if (throttledRouteInputWaypoints.length > 1) {
            startRouting();
        } else {
            setRouteResults({
                loading: false,
                success: false,
                route: [],
                messages: [{
                    message: 'Not enough points to route. Please add 2 or more points!'
                }]
            });
        }
    }, [throttledRouteInputWaypoints, routeSimplify, routeSimplifyMaxSkipPoints, routeSafetyMargin]);

    return (
        <UIContext.Provider
            value={{
                routeInputWaypoints,
                setRouteInputWaypoints,
                routeResults,
                setRouteResults,
                routeSafetyMargin,
                setRouteSafetyMargin,
                routeSimplifyMaxSkipPoints,
                setRouteSimplifyMaxSkipPoints,
                routeSimplify,
                setRouteSimplify,
                searchQuery,
                setSearchQuery,
                searchResults,
                setSearchResults,
                searchLoading,
                setSearchLoading,
                selectedSearchResult,
                setSelectedSearchResult,
                forceReload,
                setForceReload,
                commitSelectedSearchResult,
                isIosApp,
                insets,
                mobileToolbarOpen,
                setMobileToolbarOpen,
                locationTracking,
                setLocationTracking,
                track,
                queryElevation,
                attributionsOpen, 
                setAttributionsOpen,
                getAttributions
            }}
        >
            {children}
        </UIContext.Provider>
    );
}
